import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { jsPDF } from "jspdf";
import panelApi from "@/lib/panelApi";
import { apiError } from "@/lib/api";
import { brl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Loading, Empty } from "@/components/DataState";
import { toast } from "sonner";
import { Save, FileText, RotateCcw, Receipt, Lock, Unlock, UserPlus, Trash2 } from "lucide-react";
import { getBaseId } from "@/lib/api";
import { AddThirteenthCollabDialog } from "@/components/AddThirteenthCollabDialog";
import { buildThirteenthReceipts, receiptFileName } from "@/lib/thirteenthReceipt";
import { useReportLogo, addPdfLogo } from "@/lib/useLogo";
import { useBase } from "@/context/BaseContext";
import { downloadPayrollXlsx, sharePayrollWhatsApp, shareXlsxWhatsApp } from "@/lib/payrollXlsx";
import { WhatsAppMenu } from "@/components/WhatsAppMenu";
import { FileSpreadsheet, MessageCircle } from "lucide-react";
import { ReceiptWhatsApp, usePhones } from "@/components/ReceiptWhatsApp";

// INSS progressivo (tabela 2025 — ajuste o valor na linha se a tabela mudar).
const INSS = [[1518.0, 0.075], [2793.88, 0.09], [4190.83, 0.12], [8157.41, 0.14]];
export const inssOf = (v) => {
  let prev = 0; let tax = 0;
  for (const [lim, r] of INSS) { if (v <= prev) break; tax += (Math.min(v, lim) - prev) * r; prev = lim; }
  return Math.round(tax * 100) / 100;
};
// CLT: conta o mês com 15+ dias trabalhados; admissão em ano anterior = 12 meses.
export const monthsFor = (admission, year) => {
  if (!admission) return 12;
  const [y, m, d] = admission.split("-").map(Number);
  if (y < year) return 12;
  if (y > year) return 0;
  const daysInMonth = new Date(y, m, 0).getDate();
  return (12 - m) + (daysInMonth - d + 1 >= 15 ? 1 : 0);
};
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const compute = (l, parcela, firstPaidOf) => {
  const gross = r2((Number(l.salary) || 0) * (Number(l.months) || 0) / 12);
  if (parcela === 1) return { ...l, gross, first_paid: 0, inss: 0, net: r2(gross / 2 - (Number(l.other) || 0)) };
  const first = l.first_paid ?? firstPaidOf(l.user_id, gross);
  const inss = l.inssManual ? Number(l.inss) || 0 : inssOf(gross);
  return { ...l, gross, first_paid: r2(first), inss: r2(inss), net: r2(gross - first - inss - (Number(l.other) || 0)) };
};

const buildPdf = (rows, year, parcela, logo) => {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  addPdfLogo(doc, logo, { top: 10, maxH: 30 });
  const W = doc.internal.pageSize.getWidth();
  let y = 40;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text(`Central do Courier - Folha do 13o salario - ${parcela}a parcela ${year}`, 40, y); y += 24;
  const cols = parcela === 1 ? [["Colaborador", 40], ["Categoria", 250], ["Base calc.", 380], ["Meses", 460], ["13o integral", 520], ["Outros desc.", 620], ["Liquido", 720]]
    : [["Colaborador", 40], ["Categoria", 220], ["Base calc.", 330], ["Meses", 400], ["13o integral", 450], ["1a parcela", 540], ["INSS", 620], ["Outros", 680], ["Liquido", 740]];
  doc.setFontSize(8);
  cols.forEach(([t, x]) => doc.text(t, x, y)); y += 12;
  doc.setFont("helvetica", "normal");
  const ascii = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  rows.forEach((r) => {
    if (y > 560) { doc.addPage(); y = 40; }
    const vals = parcela === 1 ? [ascii(r.name).slice(0, 38), ascii(r.category), brl(r.salary), String(r.months), brl(r.gross), brl(r.other || 0), brl(r.net)]
      : [ascii(r.name).slice(0, 32), ascii(r.category), brl(r.salary), String(r.months), brl(r.gross), brl(r.first_paid), brl(r.inss), brl(r.other || 0), brl(r.net)];
    vals.forEach((v, i) => doc.text(ascii(v), cols[i][1], y)); y += 13;
  });
  y += 8; doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text(`Total liquido: ${ascii(brl(rows.reduce((a, r) => a + r.net, 0)))}`, W - 40, y, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(140);
  doc.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, 40, 580);
  return doc;
};

const numCls = "disabled:opacity-60 h-8 w-24 rounded border border-slate-700 bg-slate-950 px-1.5 text-right font-mono text-xs text-slate-100";

export function ThirteenthPanel({ year, parcela, people }) {
  const qc = useQueryClient();
  const base = getBaseId() || "";
  const logo = useReportLogo();
  const { bases } = useBase();
  const phones = usePhones().data;
  const [sentWa, setSentWa] = useState({});
  const exportXlsx = (share) => {
    const cat = (c) => { const s = String(c || "").toLowerCase(); return s.includes("admin") ? "ADMIN" : s.includes("moto") ? "Motoqueiro" : "Ciclista"; };
    const xrows = rows.map((r) => ({
      name: r.name, vehicle: cat(r.category), contract: "CLT", deliveries: null,
      cols: { E: parcela === 1 ? r.gross / 2 : r.gross, N: r.inss || 0, O: Number(r.other) || 0 },
      extras: parcela === 2 ? [{ label: "1ª parcela já paga", desc: true, value: r.first_paid || 0 }] : [],
    }));
    const baseLabel = bases.find((b) => b.id === base)?.name || "Todas as bases";
    const payload = { title_kind: "13º SALÁRIO", period_text: `13º Salário · ${parcela}ª Parcela ${year} | Base: ${baseLabel}`, rows: xrows };
    const xlsxName = `13-salario-${parcela}a-parcela-${year}.xlsx`;
    if (!share) { downloadPayrollXlsx(payload, xlsxName); return; }
    const text = `13º salário · ${parcela}ª parcela ${year} · ${baseLabel}. Total líquido: ${brl(rows.reduce((a, r) => a + r.net, 0))}.`;
    if (share === "excel") { shareXlsxWhatsApp(payload, xlsxName, `${text} Segue a planilha do 13º.`); return; }
    sharePayrollWhatsApp({
      pdfs: [{ doc: buildPdf(rows, year, parcela, logo), name: `13-salario-${parcela}a-parcela-${year}.pdf` }, { doc: buildThirteenthReceipts(rows, year, parcela, logo), name: `recibos-13-${parcela}a-parcela-${year}.pdf` }],
      text: `${text} Segue a folha e os recibos em PDF.`,
    });
  };
  const key = (p) => ["panel-thirteenth", year, p, base];
  // Folha antiga (sem base) fica com a base cujos colaboradores ela contém.
  const fetchSheet = async (p) => {
    const { sheet: s, legacy } = (await panelApi.get("/panel/thirteenth", { params: { year, parcela: p, base, bases: base ? "" : bases.map((b) => b.id).join(",") } })).data || {};
    if (s || !legacy || !people.length) return s || null;
    const ids = new Set(people.map((x) => x.id));
    const legacyIds = (legacy.lines || []).filter((l) => !l.extra_id).map((l) => l.user_id);
    const mine = base ? legacyIds.every((id) => ids.has(id)) : [...ids].every((id) => legacyIds.includes(id));
    if (!mine) return null;
    await panelApi.post("/panel/thirteenth/claim", { year, parcela: p, base });
    return legacy;
  };
  const sheet = useQuery({ queryKey: key(parcela), enabled: people.length > 0, queryFn: () => fetchSheet(parcela) });
  const first = useQuery({ queryKey: key(1), enabled: parcela === 2 && people.length > 0, queryFn: () => fetchSheet(1) });
  const adm = useQuery({ queryKey: ["panel-admissions"], queryFn: async () => (await panelApi.get("/panel/admissions")).data?.values || {} });
  const extrasQ = useQuery({ queryKey: ["panel-thirteenth-extras", year], queryFn: async () => (await panelApi.get("/panel/thirteenth-extras", { params: { year } })).data?.extras || [] });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const closed = sheet.data?.status === "closed";
  const firstPaidOf = useMemo(() => {
    const m = {}; (first.data?.lines || []).forEach((l) => { m[l.user_id] = l.net + (Number(l.other) || 0); });
    return (uid, gross) => (m[uid] ?? gross / 2);
  }, [first.data]);
  // Adicionados manualmente (cadastro de qualquer base ou fora dele); só os da base selecionada.
  const extraRows = (admMap = adm.data) => {
    const ids = new Set(people.map((p) => p.id)); const b = getBaseId();
    return (extrasQ.data || []).filter((e) => !ids.has(e.user_id) && (!b || !e.base || e.base === b)).map((e) => {
      const admission = admMap?.[e.user_id] || null;
      return compute({ user_id: e.user_id, extra_id: e.id, name: e.name, category: e.category, admission, salary: r2(e.salary), months: monthsFor(admission, year), other: 0 }, parcela, firstPaidOf);
    });
  };
  const defaults = () => [...people.map((p) => {
    const admission = adm.data?.[p.id] || null;
    return compute({ user_id: p.id, name: p.name, category: p.category, admission, salary: r2(p.salary), months: monthsFor(admission, year), other: 0 }, parcela, firstPaidOf);
  }), ...extraRows()];
  useEffect(() => {
    if (sheet.isLoading || adm.isLoading || extrasQ.isLoading || (parcela === 2 && first.isLoading)) return;
    if (sheet.data?.status === "closed") { setRows(sheet.data.lines || []); return; }
    if (!sheet.data?.lines?.length) { setRows(defaults()); return; }
    const extraIds = new Set((extrasQ.data || []).map((e) => e.id));
    const saved = sheet.data.lines.filter((l) => !l.extra_id || extraIds.has(l.extra_id)).map((l) => {
      const a = adm.data?.[l.user_id] ?? null;
      const row = { ...l, admission: a, inssManual: true };
      return a !== (l.admission ?? null) ? compute({ ...row, months: monthsFor(a, year) }, parcela, firstPaidOf) : row;
    });
    const have = new Set(saved.map((l) => l.user_id));
    setRows([...saved, ...extraRows().filter((r) => !have.has(r.user_id))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet.data, sheet.isLoading, first.data, first.isLoading, adm.isLoading, extrasQ.isLoading, parcela, year, people.length]);
  const onAdded = (e, admission) => {
    qc.setQueryData(["panel-thirteenth-extras", year], (old) => [...(old || []), e]);
    if (admission) qc.setQueryData(["panel-admissions"], (old) => ({ ...(old || {}), [e.user_id]: admission }));
    setRows((rs) => [...rs, compute({ user_id: e.user_id, extra_id: e.id, name: e.name, category: e.category, admission, salary: r2(e.salary), months: monthsFor(admission, year), other: 0 }, parcela, firstPaidOf)]);
  };
  const removeExtra = async (r) => {
    try {
      await panelApi.delete(`/panel/thirteenth-extras/${r.extra_id}`);
      qc.setQueryData(["panel-thirteenth-extras", year], (old) => (old || []).filter((x) => x.id !== r.extra_id));
      setRows((rs) => rs.filter((x) => x.user_id !== r.user_id));
      toast.success("Colaborador removido do 13º (salve o rascunho para atualizar a folha salva)");
    } catch (e) { toast.error(apiError(e)); }
  };
  const changeAdmission = (i, date) => {
    if (date && !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    if (date && Number(date.slice(0, 4)) < 1950) return;
    edit(i, { admission: date || null, months: monthsFor(date || null, year) });
  };
  const persistAdmission = async (uid, date) => {
    if ((adm.data?.[uid] ?? null) === (date || null)) return;
    try {
      await panelApi.put(`/panel/admissions/${uid}`, { date: date || null });
      qc.setQueryData(["panel-admissions"], (old) => {
        const next = { ...(old || {}) };
        if (date) next[uid] = date; else delete next[uid];
        return next;
      });
      toast.success("Data de admissão salva");
    } catch (e) { toast.error(apiError(e)); }
  };
  const partsOf = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p.parts || {}])), [people]);
  const baseTitle = (uid) => Object.entries(partsOf[uid] || {}).map(([k, v]) => `${k}: ${brl(v)}`).join(" + ");
  const receipt = (r) => buildThirteenthReceipts([r], year, parcela, logo).save(receiptFileName(r.name, year, parcela));
  const edit = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? compute({ ...r, ...patch }, parcela, firstPaidOf) : r)));
  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await qc.invalidateQueries({ queryKey: ["panel-thirteenth"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const save = (close) => act(() => panelApi.put("/panel/thirteenth", { year, parcela, base, lines: rows, close }), close ? `Folha da ${parcela}ª parcela do 13º fechada${base ? " nesta base" : ""}` : "Rascunho salvo");
  const reopen = () => act(() => panelApi.post("/panel/thirteenth/reopen", { year, parcela, base }), "Folha reaberta");
  const total = rows.reduce((a, r) => a + (r.net || 0), 0);
  if (sheet.isLoading || adm.isLoading || extrasQ.isLoading) return <Loading />;
  return (
    <div className="space-y-4" data-testid={`thirteenth-panel-${parcela}`}>
      <AddThirteenthCollabDialog open={addOpen} onClose={() => setAddOpen(false)} year={year} existingIds={new Set(rows.map((r) => r.user_id))} onAdded={onAdded} />
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-orange-300">13º salário · {parcela}ª parcela · {year}</p>
          <p className="text-xs text-slate-500">
            {parcela === 1 ? "50% do 13º proporcional (base de cálculo × meses/12), sem INSS. Base = soma dos proventos da configuração, sem benefícios." : "13º integral − 1ª parcela − INSS (tabela 2025, editável). IRRF: lance em Outros descontos."}
            {" "}CLT da folha + adicionados manualmente. {sheet.data ? `${closed ? "Fechada" : "Rascunho salvo"} em ${new Date(sheet.data.updated_at).toLocaleString("pt-BR")}.` : "Aberta — ainda não salva."}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <span className={`self-center rounded-full px-3 py-1 text-xs font-semibold ${closed ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid="thirteenth-status">{closed ? "Fechada · valores congelados" : "Aberta"}</span>
          <Button variant="outline" className="border-slate-700 text-slate-300" disabled={closed} onClick={() => setAddOpen(true)} data-testid="thirteenth-add-collab"><UserPlus className="h-4 w-4" /> Adicionar colaborador</Button>
          <Button variant="outline" className="border-slate-700 text-slate-300" disabled={closed} onClick={() => setRows(defaults())} data-testid="thirteenth-recalc"><RotateCcw className="h-4 w-4" /> Recalcular</Button>
          <Button variant="outline" className="border-slate-700 text-slate-300" disabled={!rows.length} onClick={() => buildThirteenthReceipts(rows, year, parcela, logo).save(`recibos-13-${parcela}a-parcela-${year}.pdf`)} data-testid="thirteenth-receipts-all"><Receipt className="h-4 w-4" /> Recibos (todos)</Button>
          <Button variant="outline" className="border-slate-700 text-slate-300" disabled={!rows.length} onClick={() => buildPdf(rows, year, parcela, logo).save(`13-salario-${parcela}a-parcela-${year}.pdf`)} data-testid="thirteenth-pdf"><FileText className="h-4 w-4" /> Baixar PDF</Button>
          <Button variant="outline" className="border-slate-700 text-slate-300" disabled={!rows.length} onClick={() => exportXlsx()} data-testid="thirteenth-xlsx"><FileSpreadsheet className="h-4 w-4" /> Exportar Excel</Button>
          <WhatsAppMenu testId="thirteenth-whatsapp" disabled={!rows.length} onPdf={() => exportXlsx("pdf")} onExcel={() => exportXlsx("excel")} />
          {closed && sheet.data?.consolidated ? (
            <span className="self-center text-xs text-slate-400" data-testid="thirteenth-consolidated">Fechada automaticamente: todas as bases estão fechadas. Para editar, reabra a base.</span>
          ) : closed ? (
            <Button className="bg-amber-600 text-white hover:bg-amber-700" disabled={busy} onClick={reopen} data-testid="thirteenth-reopen"><Unlock className="h-4 w-4" /> Reabrir folha</Button>
          ) : (
            <>
              <Button variant="outline" className="border-slate-700 text-slate-300" disabled={busy || !rows.length} onClick={() => save(false)} data-testid="thirteenth-save"><Save className="h-4 w-4" /> Salvar rascunho</Button>
              <Button className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={busy || !rows.length} onClick={() => save(true)} data-testid="thirteenth-close"><Lock className="h-4 w-4" /> Fechar folha</Button>
            </>
          )}
        </div>
      </div>
      {!rows.length ? <Empty label="Nenhum colaborador CLT na folha deste mês" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
          <table className="w-full min-w-[1180px] text-sm" data-testid="thirteenth-table">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3">Colaborador</th><th className="px-3 py-3">Categoria</th><th className="px-3 py-3">Admissão</th>
                <th className="px-3 py-3 text-right">Base de cálculo</th><th className="px-3 py-3 text-right">Meses</th>
                <th className="px-3 py-3 text-right">13º integral</th>
                {parcela === 2 && <><th className="px-3 py-3 text-right">1ª parcela</th><th className="px-3 py-3 text-right">INSS</th></>}
                <th className="px-3 py-3 text-right">Outros desc.</th><th className="px-3 py-3 text-right">Líquido</th><th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((r, i) => (
                <tr key={r.user_id} data-testid={`thirteenth-row-${r.user_id}`}>
                  <td className="px-3 py-2 text-slate-100">
                    <span className="flex items-center gap-1.5">
                      {r.name}
                      {r.extra_id && <span className="rounded bg-sky-500/15 px-1 text-[10px] text-sky-300" data-testid={`thirteenth-manual-tag-${r.user_id}`}>manual</span>}
                      {r.extra_id && !closed && <button onClick={() => removeExtra(r)} title="Remover do 13º" className="text-slate-500 hover:text-rose-400" data-testid={`thirteenth-remove-${r.user_id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-400">{r.category}</td>
                  <td className="px-3 py-2"><input type="date" disabled={closed} value={r.admission || ""} onChange={(e) => changeAdmission(i, e.target.value)} onBlur={() => persistAdmission(r.user_id, r.admission)} className="h-8 rounded border border-slate-700 bg-slate-950 px-1.5 text-xs text-slate-100 [color-scheme:dark]" data-testid={`thirteenth-admission-${r.user_id}`} /></td>
                  <td className="px-3 py-2 text-right"><input type="number" step="0.01" disabled={closed} value={r.salary} title={baseTitle(r.user_id)} onChange={(e) => edit(i, { salary: e.target.value })} className={numCls} data-testid={`thirteenth-salary-${r.user_id}`} /></td>
                  <td className="px-3 py-2 text-right"><input type="number" min="0" max="12" disabled={closed} value={r.months} onChange={(e) => edit(i, { months: Math.max(0, Math.min(12, Number(e.target.value) || 0)) })} className={`${numCls} w-16`} data-testid={`thirteenth-months-${r.user_id}`} /></td>
                  <td className="px-3 py-2 text-right font-mono text-slate-300">{brl(r.gross)}</td>
                  {parcela === 2 && (
                    <>
                      <td className="px-3 py-2 text-right font-mono text-slate-400">{brl(r.first_paid)}</td>
                      <td className="px-3 py-2 text-right"><input type="number" step="0.01" disabled={closed} value={r.inss} onChange={(e) => edit(i, { inss: e.target.value, inssManual: true })} className={numCls} data-testid={`thirteenth-inss-${r.user_id}`} /></td>
                    </>
                  )}
                  <td className="px-3 py-2 text-right"><input type="number" step="0.01" disabled={closed} value={r.other || 0} onChange={(e) => edit(i, { other: e.target.value })} className={numCls} data-testid={`thirteenth-other-${r.user_id}`} /></td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-300" data-testid={`thirteenth-net-${r.user_id}`}>{brl(r.net)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right"><Button size="sm" variant="ghost" className="text-slate-300 hover:text-orange-300" onClick={() => receipt(r)} data-testid={`thirteenth-receipt-${r.user_id}`}><Receipt className="h-4 w-4" /> Recibo</Button>
                    <ReceiptWhatsApp row={r} phones={phones} sent={!!sentWa[r.user_id]} onSent={(id) => setSentWa((s) => ({ ...s, [id]: true }))} year={year} parcela={parcela} logo={logo} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-700">
                <td colSpan={parcela === 2 ? 9 : 7} className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-400">Total líquido</td>
                <td className="px-3 py-3 text-right font-mono text-lg font-bold text-white" data-testid="thirteenth-total">{brl(total)}</td><td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
